import { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Calendar, Users, Phone,
  MessageSquare, Settings, LogOut, Menu, X,
  ChevronRight, Zap
} from "lucide-react";

const navItems = [
  { path: "/admin", icon: LayoutDashboard, label: "Dashboard", exact: true },
  { path: "/admin/appointments", icon: Calendar, label: "Appointments" },
  { path: "/admin/patients", icon: Users, label: "Patients" },
  { path: "/admin/calls", icon: Phone, label: "Call Logs" },
  { path: "/admin/whatsapp", icon: MessageSquare, label: "WhatsApp" },
  { path: "/admin/settings", icon: Settings, label: "Settings" },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const username = localStorage.getItem("clinic_admin_user") || "Admin";
  const clinicName = localStorage.getItem("clinic_name") || "Careline";

  const logout = () => {
    localStorage.removeItem("clinic_admin_token");
    localStorage.removeItem("clinic_admin_user");
    navigate("/admin/login");
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="h-16 flex items-center px-5 border-b border-white/5">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-violet-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
            <Zap className="w-4 h-4 text-white" />
          </div>
          <div>
            <span className="font-bold font-outfit text-white tracking-tight">{clinicName}</span>
            <span className="block text-[10px] text-slate-500 leading-none -mt-0.5 font-medium tracking-widest uppercase">Admin</span>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {navItems.map(item => (
          <NavLink
            key={item.path}
            to={item.path}
            data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
            end={item.exact}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive: linkActive }) =>
              `sidebar-item ${linkActive ? "active" : ""}`
            }
          >
            <item.icon className="w-4 h-4 flex-shrink-0" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* User */}
      <div className="p-3 border-t border-white/5">
        <div className="flex items-center gap-3 px-2 py-2 rounded-lg">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500/30 to-violet-500/30 border border-white/10 flex items-center justify-center text-cyan-400 font-semibold text-sm">
            {username.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-white truncate">{username}</div>
            <div className="text-xs text-slate-500">Administrator</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="text-slate-500 hover:text-red-400 transition-colors p-1 rounded-md hover:bg-red-500/10"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#0F1117] overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-60 bg-[#0A0D14] border-r border-white/5 flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-60 bg-[#0A0D14] border-r border-white/5 shadow-2xl">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="h-16 bg-[#0A0D14] border-b border-white/5 flex items-center px-4 md:px-6 gap-4 flex-shrink-0">
          <button
            data-testid="mobile-sidebar-toggle"
            className="md:hidden text-slate-400 hover:text-white transition-colors"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1" />
          <a
            href="/"
            className="text-xs text-slate-500 hover:text-cyan-400 transition-colors flex items-center gap-1.5 border border-white/5 rounded-lg px-3 py-1.5 hover:border-cyan-500/20"
          >
            View Landing Page
            <ChevronRight className="w-3 h-3" />
          </a>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-[#0F1117]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
